import { PLATFORM_LIMITS } from './limits.js';
import { PLAN_FEATURES, type PlanName } from './plans.js';
import { isXThread } from './post-type.js';
import { xSegments, xTruncate, xUrls, xWeightedLength } from './x-text.js';

/**
 * X post credits: the monthly allowance that meters posting to X.
 *
 * X's API is pay-per-use with no free tier: $0.015 for a post, $0.20 for a
 * post that contains a link (docs.x.com/x-api/getting-started/pricing). Every
 * plan otherwise promises unlimited posts, so X posts are counted in credits
 * that follow that price: a post costs 1 credit, a post with a link 13
 * (0.20 / 0.015, rounded). Each post of a thread is a post of its own.
 *
 * The allowance belongs to the account (ADR-006), one pool across its brands,
 * per calendar month. It is the `x.post_credits` feature flag's per-plan
 * limit, so an admin can change it without a deploy; X_POST_CREDITS_BY_PLAN is
 * used only when the flag row is missing.
 *
 * The API reserves a post's credits when it is scheduled or published (in the
 * month it will publish), the worker checks again when it publishes (against
 * the reservation's month), and the reservation is given back when the X post
 * fails for good, is cancelled or is deleted before it goes out. One ledger
 * row per post (`x.post`), whose amount an edit adjusts.
 *
 * Spent is spent: the moment X accepts the first post of it, the worker marks
 * the row consumed (`consumedAt`), re-creating it if it was given back in the
 * meantime. A consumed row is never given back, whatever happens to the post
 * afterwards (deleted, deleted from X, a thread that stopped part way, which
 * keeps only what its posted part cost). Publishing the post to X again
 * later is a new charge: the consumed row is kept under
 * `spentXCreditsSourceId` and a new reservation is taken.
 */

/** What a post without a link costs. */
export const X_POST_CREDIT_COST = 1;

/** What a post with a link costs: X charges $0.20 for it against $0.015. */
export const X_LINK_POST_CREDIT_COST = 13;

/** The feature flag whose per-plan limits are each plan's monthly X credits. */
export const X_POST_CREDITS_FLAG = 'x.post_credits';

/** The error code of a post that would spend more X credits than are left. */
export const X_POST_LIMIT_REACHED = 'X_POST_LIMIT_REACHED';

/** The published monthly X credits of each plan; used only when the flag row is missing. */
export const X_POST_CREDITS_BY_PLAN: Readonly<Record<PlanName, number>> = {
  STARTER: PLAN_FEATURES.STARTER.xPostCredits,
  CLASSIC: PLAN_FEATURES.CLASSIC.xPostCredits,
  PRO: PLAN_FEATURES.PRO.xPostCredits,
  AGENCY: PLAN_FEATURES.AGENCY.xPostCredits,
};

/**
 * The credits a run of X posts costs: 13 for each post whose text holds a
 * link (as X detects one, bare domains included), 1 for each other post.
 */
export function xPostCredits(segments: readonly string[]): number {
  let total = 0;
  for (const segment of segments) {
    total += xUrls(segment).length > 0 ? X_LINK_POST_CREDIT_COST : X_POST_CREDIT_COST;
  }
  return total;
}

/**
 * A word too heavy for one post (a long run with no space, as in Chinese or
 * Japanese text) cut into pieces of at most `maxLen`, between graphemes and
 * never inside a link.
 */
function xSplitLongWord(word: string, maxLen: number): string[] {
  const pieces: string[] = [];
  let current = '';
  let weight = 0;
  for (const segment of xSegments(word)) {
    if (weight + segment.weight > maxLen && current) {
      pieces.push(current);
      current = '';
      weight = 0;
    }
    current += segment.text;
    weight += segment.weight;
  }
  if (current) pieces.push(current);
  return pieces;
}

/**
 * A thread's posts: the text cut at word boundaries, each piece at most
 * `threadBreakAt` by X's weighted count (links 23, CJK and emoji 2). A word
 * heavier than that on its own is cut between graphemes, so no piece is one
 * X would refuse. The worker publishes exactly these, so the credits counted
 * are the posts sent.
 *
 * The whitespace the author typed between two words stays as it was inside a
 * piece (line breaks and blank lines included); only the whitespace where one
 * piece ends and the next begins is dropped.
 */
export function xThreadChunks(text: string): string[] {
  const maxLen = PLATFORM_LIMITS.TWITTER.threadBreakAt;
  const chunks: string[] = [];
  let current = '';

  // [word, separator, word, separator, …] with the separators kept.
  const tokens = text.trim().split(/(\s+)/);
  for (let i = 0; i < tokens.length; i += 2) {
    const whole = tokens[i] ?? '';
    if (!whole) continue;
    const separator = i > 0 ? (tokens[i - 1] ?? ' ') : '';
    const words = xWeightedLength(whole) > maxLen ? xSplitLongWord(whole, maxLen) : [whole];
    words.forEach((word, k) => {
      // Pieces of one long word were contiguous: nothing between them.
      const sep = k === 0 ? separator : '';
      const candidate = current ? `${current}${sep}${word}` : word;
      if (xWeightedLength(candidate) > maxLen && current) {
        chunks.push(current);
        current = word;
      } else {
        current = candidate;
      }
    });
  }

  if (current) chunks.push(current);
  return chunks;
}

/**
 * The posts X receives for a caption as it is published: a thread's pieces
 * (the post type compared through isXThread: 'Thread' and 'thread' alike),
 * or the one post (a media-only post is a post too).
 */
export function xPostSegments(text: string, postType?: string | null): string[] {
  if (isXThread(postType)) {
    const chunks = xThreadChunks(text);
    return chunks.length > 0 ? chunks : [text];
  }
  return [text];
}

/**
 * A caption as X receives it: trimmed, and a post that is not a thread cut
 * to X's 280 by weight (between graphemes, never inside a link, "..." after).
 * A thread keeps its whole text; xThreadChunks splits it. The API formatter
 * publishes exactly this, and the composer counts credits on it, so a link
 * past the cut costs nothing on either side.
 */
export function xPublishedCaption(text: string, postType?: string | null): string {
  const trimmed = text.trim();
  if (isXThread(postType)) return trimmed;
  const max = PLATFORM_LIMITS.TWITTER.chars;
  return xWeightedLength(trimmed) > max ? xTruncate(trimmed, max) : trimmed;
}

/** The credits a caption costs on X, as published with this post type. */
export function xCreditsForCaption(text: string, postType?: string | null): number {
  return xPostCredits(xPostSegments(text, postType));
}

/**
 * The source id a post's consumed X credits are kept under once the post is
 * published to X again (a new charge, with a new reservation under the post
 * id). It still counts in its own month and is never given back.
 */
export function spentXCreditsSourceId(postId: string, consumedAt: Date): string {
  return `${postId}:spent:${consumedAt.getTime()}`;
}

/**
 * The time zone every allowance's month is counted in: India Standard Time
 * (UTC+05:30, no daylight saving), where the product is sold. Allowances
 * reset at midnight IST on the 1st, whatever the server's own zone (UTC on
 * EC2 would reset them at 05:30 IST).
 */
export const USAGE_MONTH_UTC_OFFSET_MINUTES = 330;

/**
 * The usage month an instant falls in: midnight IST on the 1st up to the
 * next 1st. The one month boundary of every allowance (posts, AI, posters,
 * X credits), in the API and the worker alike.
 */
export function usageMonth(at: Date = new Date()): { start: Date; end: Date } {
  const offsetMs = USAGE_MONTH_UTC_OFFSET_MINUTES * 60_000;
  const local = new Date(at.getTime() + offsetMs);
  const year = local.getUTCFullYear();
  const month = local.getUTCMonth();
  return {
    start: new Date(Date.UTC(year, month, 1) - offsetMs),
    end: new Date(Date.UTC(year, month + 1, 1) - offsetMs),
  };
}

/** The usage month of X credits: the same month as every other allowance. */
export function xUsageMonth(at: Date = new Date()): { start: Date; end: Date } {
  return usageMonth(at);
}
